import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import { MessageSquare, Phone, Users, Mail, CheckSquare, Plus, Loader2, Filter, ChevronDown } from 'lucide-react';

const ACTIVITY_TYPES = {
  note: { label: 'Note', bgColor: 'bg-blue-100', textColor: 'text-blue-700', borderColor: 'border-blue-200' },
  call: { label: 'Call', bgColor: 'bg-green-100', textColor: 'text-green-700', borderColor: 'border-green-200' },
  meeting: { label: 'Meeting', bgColor: 'bg-purple-100', textColor: 'text-purple-700', borderColor: 'border-purple-200' },
  email: { label: 'Email', bgColor: 'bg-orange-100', textColor: 'text-orange-700', borderColor: 'border-orange-200' },
  task: { label: 'Task', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', borderColor: 'border-yellow-200' },
  stage_changed: { label: 'Stage Changed', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700', borderColor: 'border-indigo-200' },
  opportunity_created: { label: 'Opportunity', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700', borderColor: 'border-emerald-200' },
};

function ActivityItem({ activity, isLast }) {
  const typeConfig = ACTIVITY_TYPES[activity.activity_type] || ACTIVITY_TYPES.note;
  const classes = typeConfig.bgColor + ' ' + typeConfig.textColor + ' ' + typeConfig.borderColor;
  
  return (
    <div className="flex gap-4 relative">
      {!isLast && <div className="absolute left-5 top-10 bottom-0 w-px bg-border" />}
      <div className={'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border z-10 ' + classes}>
        <MessageSquare className="h-4 w-4" />
      </div>
      <div className="flex-1 pb-6">
        <div className="flex items-start justify-between gap-2">
          <div>
            <Badge variant="outline" className={'text-xs rounded-sm ' + classes}>
              {typeConfig.label}
            </Badge>
            <p className="text-sm mt-1">{activity.content}</p>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <p>{formatDateTime(activity.created_at)}</p>
            <p>{activity.created_by_name}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddActivityDialog({ open, onOpenChange, type, title, onSave, loading }) {
  const [content, setContent] = useState('');
  const [callOutcome, setCallOutcome] = useState('');

  function handleSave() {
    if (!content.trim()) {
      toast.error('Please enter content');
      return;
    }
    onSave({
      activity_type: type,
      content: content,
      call_outcome: type === 'call' ? callOutcome : null,
    });
    setContent('');
    setCallOutcome('');
  }

  function handleClose(isOpen) {
    if (!isOpen) {
      setContent('');
      setCallOutcome('');
    }
    onOpenChange(isOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {type === 'call' && (
            <div className="space-y-2">
              <Label>Call Outcome</Label>
              <Select value={callOutcome} onValueChange={setCallOutcome}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">Connected</SelectItem>
                  <SelectItem value="voicemail">Left Voicemail</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Details</Label>
            <Textarea 
              value={content} 
              onChange={function(e) { setContent(e.target.value); }} 
              placeholder="Enter details..." 
              rows={4} 
              className="rounded-sm resize-none" 
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={function() { handleClose(false); }} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading} className="rounded-sm">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ContactTimeline = forwardRef(function ContactTimeline({ contactId, onActivityLogged }, ref) {
  const api = useApi();
  const [activities, setActivities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [dialogType, setDialogType] = useState(null);

  // Expose imperative trigger so parents (e.g. ContactDetail Quick Action Bar) can open the dialog
  useImperativeHandle(ref, () => ({
    openDialog: (type) => setDialogType(type),
  }), []);

  useEffect(function() {
    fetchActivities(true);
  }, [contactId, filter]);

  function fetchActivities(reset) {
    const currentSkip = reset ? 0 : skip;
    let url = '/contacts/' + contactId + '/timeline?limit=25&skip=' + currentSkip;
    if (filter !== 'all') {
      url += '&activity_type=' + filter;
    }
    api.get(url).then(function(data) {
      if (reset) {
        setActivities(data.activities);
        setSkip(25);
      } else {
        setActivities(function(prev) { return prev.concat(data.activities); });
        setSkip(function(prev) { return prev + 25; });
      }
      setTotalCount(data.total_count);
      setHasMore(data.has_more);
    }).catch(function(err) {
      console.error('Failed to fetch timeline:', err);
    });
  }

  function loadMore() {
    setLoadingMore(true);
    fetchActivities(false);
    setLoadingMore(false);
  }

  function handleAddActivity(data) {
    api.post('/contacts/' + contactId + '/timeline', data).then(function() {
      toast.success('Activity logged');
      setDialogType(null);
      fetchActivities(true);
      // Notify parent so it can refetch the contact and surface a fresh last_contacted_date
      if (typeof onActivityLogged === 'function') onActivityLogged(data);
    }).catch(function(err) {
      toast.error(err.message);
    });
  }

  function renderActivityList() {
    if (activities.length === 0) {
      return (
        <div className="text-center py-12">
          <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">No activities yet</p>
          <Button onClick={function() { setDialogType('note'); }} className="rounded-sm">
            <Plus className="h-4 w-4 mr-2" />Add First Note
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-0">
        {activities.map(function(activity, index) {
          return (
            <ActivityItem 
              key={activity.id} 
              activity={activity} 
              isLast={index === activities.length - 1} 
            />
          );
        })}
        {hasMore && (
          <div className="pt-4 text-center">
            <Button variant="outline" onClick={loadMore} disabled={loadingMore} className="rounded-sm" data-testid="load-more-btn">
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ChevronDown className="h-4 w-4 mr-2" />}
              Load More
            </Button>
          </div>
        )}
      </div>
    );
  }

  function getDialogTitle() {
    if (dialogType === 'note') return 'Add Note';
    if (dialogType === 'call') return 'Log Call';
    if (dialogType === 'meeting') return 'Log Meeting';
    if (dialogType === 'email') return 'Log Email';
    if (dialogType === 'task') return 'Add Task';
    return '';
  }

  return (
    <Card className="rounded-sm border" data-testid="contact-timeline">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Activity Timeline ({totalCount})
          </CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] rounded-sm" data-testid="timeline-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activities</SelectItem>
              <SelectItem value="notes">Notes</SelectItem>
              <SelectItem value="emails">Emails</SelectItem>
              <SelectItem value="stage_changes">Stage Changes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="inline-flex items-center rounded-full border bg-background p-1 mt-4 gap-0.5 flex-wrap" data-testid="timeline-action-pills">
          <button onClick={function() { setDialogType('note'); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-muted text-muted-foreground hover:text-foreground transition" data-testid="add-note-btn">
            <MessageSquare className="h-3 w-3" /> Note
          </button>
          <button onClick={function() { setDialogType('call'); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-muted text-muted-foreground hover:text-foreground transition" data-testid="add-call-btn">
            <Phone className="h-3 w-3" /> Call
          </button>
          <button onClick={function() { setDialogType('meeting'); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-muted text-muted-foreground hover:text-foreground transition" data-testid="add-meeting-btn">
            <Users className="h-3 w-3" /> Meeting
          </button>
          <button onClick={function() { setDialogType('email'); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-muted text-muted-foreground hover:text-foreground transition" data-testid="add-email-btn">
            <Mail className="h-3 w-3" /> Email
          </button>
          <button onClick={function() { setDialogType('task'); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-muted text-muted-foreground hover:text-foreground transition" data-testid="add-task-btn">
            <CheckSquare className="h-3 w-3" /> Task
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {renderActivityList()}
      </CardContent>
      <AddActivityDialog 
        open={dialogType !== null} 
        onOpenChange={function(open) { if (!open) setDialogType(null); }} 
        type={dialogType || 'note'} 
        title={getDialogTitle()} 
        onSave={handleAddActivity} 
        loading={api.loading} 
      />
    </Card>
  );
});

export default ContactTimeline;
